import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, Filter } from 'lucide-react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface AuditEntry {
  id: string;
  created_at: string;
  user_id: string | null; // null for automated actions
  action: string;
  entity_type: string;
  details: any;
  user_email?: string;
}

export function SuperAdminAuditLog() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState<string>('all');

  async function loadAuditLog() {
    try {
      setLoading(true);

      let query = supabase
        .from('audit_log')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (actionFilter !== 'all') {
        query = query.ilike('action', `%${actionFilter}%`);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Get user emails. Automated actions have no user_id at all, so they are
      // excluded from the lookup — passing null through would make the RPC
      // return nothing and label every row 'Unknown'.
      const userIds = [...new Set((data ?? []).map((e) => e.user_id).filter(Boolean))];
      const { data: users } = userIds.length
        ? await supabase.rpc('get_users_by_ids', { user_ids: userIds })
        : { data: [] };

      const entriesWithEmails =
        data?.map((entry) => ({
          ...entry,
          user_email: !entry.user_id
            ? 'System'
            : users?.find((u: any) => u.id === entry.user_id)?.email || 'Unknown',
        })) || [];

      setEntries(entriesWithEmails);
    } catch (error: any) {
      toast.error('Failed to load audit log: ' + error.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAuditLog();
  }, [actionFilter]);

  const getActionBadgeVariant = (action: string) => {
    if (action.includes('DELETE') || action.includes('REVOKE')) return 'destructive';
    if (action.includes('CREATE') || action.includes('GRANT')) return 'default';
    if (action.includes('UPDATE') || action.includes('MODIFY')) return 'secondary';
    return 'outline';
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Platform Audit Log</CardTitle>
            <CardDescription>Track all administrative actions across the platform</CardDescription>
          </div>
          <div className="flex gap-2">
            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger className="w-[180px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="SUPER_ADMIN">Super Admin</SelectItem>
                <SelectItem value="USER_DELETED">User Deleted</SelectItem>
                <SelectItem value="ROLE_GRANTED">Role Granted</SelectItem>
                <SelectItem value="ROLE_REVOKED">Role Revoked</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={loadAuditLog} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8 text-muted-foreground">Loading audit log...</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Timestamp</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Entity</TableHead>
                <TableHead>Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell className="font-mono text-xs">
                    {new Date(entry.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-sm">{entry.user_email}</TableCell>
                  <TableCell>
                    <Badge variant={getActionBadgeVariant(entry.action)}>{entry.action}</Badge>
                  </TableCell>
                  <TableCell className="text-sm">{entry.entity_type}</TableCell>
                  <TableCell className="max-w-xs truncate text-xs text-muted-foreground">
                    {JSON.stringify(entry.details)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
